/* DP4Y Cart Rules
   - Free stones: enforce exactly 1 when cart has other (non-addon) items; remove when cart empty of other items
   - Photo check: allow max 1; also keep the "Foto check actief" state in sync

   This script is intentionally defensive:
   - If Liquid could not resolve variant ids (e.g. product not published), we fallback to handles and cart line keys.
   - We avoid calling Shopify endpoints with invalid ids (prevents the red error banner).
*/
(function () {
  'use strict';

  const RULES = (window.DP4Y_CART_RULES || {});

  const PHOTO_HANDLE = RULES.photoCheckHandle || 'foto-check-service';
  const STONES_HANDLE = RULES.freeStonesHandle || 'extra-steentjes-gratis';
  const PHOTO_ID_LIQUID = Number(RULES.photoCheckVariantId || 0);
  const STONES_ID_LIQUID = Number(RULES.freeStonesVariantId || 0);

  let enforcing = false;
  let miniPhotoToggleBusy = false;
  let pendingPhotoChoice = null;
  let enforcePending = false;
  let enforceTimer;
  let lastOrphanAttempt = null;
  let cachedProductVariantIds = Object.create(null); // handle -> variant_id

  async function fetchJSON(url, options) {
    const res = await fetch(url, Object.assign({ credentials: 'same-origin' }, options || {}));
    const txt = await res.text();
    let data;
    try { data = JSON.parse(txt); } catch (e) { data = null; }
    if (!res.ok) {
      const msg = (data && (data.description || data.message)) || res.statusText || 'Request failed';
      throw new Error(msg);
    }
    return data;
  }

  async function fetchCart() {
    const cart = await fetchJSON('/cart.js', { method: 'GET' });
    if (!cart || !Array.isArray(cart.items) || !cart.attributes ||
        typeof cart.attributes !== 'object' || Array.isArray(cart.attributes)) {
      throw new Error('Incomplete cart response; no rule applied');
    }
    return cart;
  }

  function getLineItemsByHandle(cart, handle) {
    const h = String(handle || '').trim();
    if (!h) return [];
    const items = cart.items || [];
    // Shopify /cart.js does not always include a "line" field; compute it from array index (1-based).
    return items
      .map((it, idx) => Object.assign({ line: idx + 1 }, it))
      .filter(it => String(it.handle || '') === h);
  }

  function getPhotoCheckLine(cart) {
    const items = (cart && cart.items) || [];
    const byVariant = PHOTO_ID_LIQUID
      ? items.find(it => Number(it.variant_id) === PHOTO_ID_LIQUID && Number(it.quantity || 0) > 0)
      : null;
    if (byVariant) return byVariant;

    return items.find(it => {
      const handle = String(it.handle || '');
      return Number(it.quantity || 0) > 0 && (handle === PHOTO_HANDLE || handle === 'foto-check');
    }) || null;
  }

  function cartHasPhotoCheck(cart) {
    return !!getPhotoCheckLine(cart);
  }

  function sumQty(items) {
    return (items || []).reduce((s, it) => s + Number(it.quantity || 0), 0);
  }

  function getOtherItems(cart) {
    return (cart.items || []).filter(it => {
      const h = String(it.handle || '');
      return Number(it.quantity || 0) > 0 && !isPhotoCheckItem(it) &&
        h !== STONES_HANDLE &&
        !(STONES_ID_LIQUID && Number(it.variant_id) === STONES_ID_LIQUID);
    });
  }

  function isPhotoCheckItem(item) {
    return Number(item.quantity || 0) > 0 && (
      (PHOTO_ID_LIQUID && Number(item.variant_id) === PHOTO_ID_LIQUID) ||
      item.handle === PHOTO_HANDLE || item.handle === 'foto-check'
    );
  }

  function photoAttributeChanges(cart, hasPhoto) {
    const expected = {
      'Foto check': hasPhoto ? 'Voorbeeld binnen 3 uur via WhatsApp / e-mail.' : '',
      photo_check: hasPhoto ? '1' : '',
      photo_check_active: hasPhoto ? '1' : ''
    };
    const changes = {};
    Object.keys(expected).forEach(key => {
      if (String((cart.attributes || {})[key] || '') !== expected[key]) {
        changes[key] = expected[key];
      }
    });
    return changes;
  }

  async function removeOrphanPhotoCheck(cart) {
    if (getOtherItems(cart).length) {
      lastOrphanAttempt = null;
      return false;
    }
    const photoLines = (cart.items || []).filter(isPhotoCheckItem);
    const attributes = photoAttributeChanges(cart, false);
    if (!photoLines.length && !Object.keys(attributes).length) {
      lastOrphanAttempt = null;
      return true;
    }
    if (photoLines.some(item => !item.key)) {
      throw new Error('Photo check line key missing; no cleanup attempted');
    }
    const updates = {};
    photoLines.forEach(item => { updates[item.key] = 0; });
    const signature = JSON.stringify({
      keys: Object.keys(updates).sort(), attributes
    });
    if (signature === lastOrphanAttempt) return true;
    lastOrphanAttempt = signature;
    try {
      await fetchJSON('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, attributes })
      });
    } finally {
      const confirmed = await fetchCart();
      syncMiniPhotoCheckbox(confirmed);
      await refreshMiniCart();
      if ((confirmed.items || []).some(isPhotoCheckItem) ||
          Object.keys(photoAttributeChanges(confirmed, false)).length) {
        throw new Error('Photo check cleanup not confirmed; no automatic retry');
      }
    }
    return true;
  }

  async function changeByLine(line, quantity) {
    // Shopify: /cart/change.js expects line (1-based) OR id.
    // NOTE: line numbers can shift when the cart is updated asynchronously, so prefer changeById.
    if (!line || line < 1) return;
    await fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
    });
  }

  async function changeById(variantId, quantity) {
    // Robust against line-number shifts.
    if (!variantId) return;
    await fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) })
    });
  }

  async function updateAttributes(attrs) {
    if (!attrs || typeof attrs !== 'object') return;
    await fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  async function resolveVariantIdFromHandle(handle) {
    const h = String(handle || '').trim();
    if (!h) return 0;
    if (cachedProductVariantIds[h]) return cachedProductVariantIds[h];
    try {
      const p = await fetchJSON(`/products/${encodeURIComponent(h)}.js`, { method: 'GET' });
      const v = (p && p.variants && p.variants[0] && p.variants[0].id) ? Number(p.variants[0].id) : 0;
      if (v) cachedProductVariantIds[h] = v;
      return v;
    } catch (e) {
      return 0;
    }
  }

  async function addVariant(variantId, qty) {
    const id = Number(variantId || 0);
    const q = Number(qty || 0);
    if (!id || q <= 0) return;
    await fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, quantity: q })
    });
  }

  async function removePhotoCheckFromCart(cart) {
    const found = getPhotoCheckLine(cart);
    if (!found || !found.key) return;
    await fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: found.key, quantity: 0 })
    });
  }

  async function enforceFreeStones(cart) {
    // We show “Altijd genoeg steentjes” as an included guarantee in the cart UI,
    // but we do NOT want a separate line-item in the cart for it.
    // So if the product is present (manually added or otherwise), remove it.
    const stonesLines = getLineItemsByHandle(cart, STONES_HANDLE);
    for (let i = 0; i < stonesLines.length; i++) {
      if (Number(stonesLines[i].quantity) > 0) {
        await changeById(stonesLines[i].variant_id, 0);
        return true;
      }
    }
    return false;
  }

  async function enforcePhotoCheck(cart) {
    const photoLines = getLineItemsByHandle(cart, PHOTO_HANDLE);
    const photoQty = sumQty(photoLines);

    // Enforce max 1 (do not auto-add)
    if (photoLines.length > 0) {
      // Keep first line at qty 1, remove rest
      const first = photoLines[0];
      if (Number(first.quantity) > 1) {
        await changeById(first.variant_id, 1);
        return { changed: true, hasPhoto: true };
      }
      for (let i = 1; i < photoLines.length; i++) {
        if (Number(photoLines[i].quantity) > 0) {
          await changeById(photoLines[i].variant_id, 0);
          return { changed: true, hasPhoto: true };
        }
      }
    }

    return { changed: false, hasPhoto: photoQty > 0 };
  }

  async function syncPhotoActiveFlag(hasPhoto, cart) {
    // Keep badge state consistent with cart contents.
    // Some theme templates reference cart.attributes['Foto check'] (with a space)
    // while older scripts used cart.attributes.photo_check. We write both.
    // Don't store a numeric flag in the human-facing attribute; the theme
    // prints this attribute as copy in the cart UI.
    try {
      const changes = photoAttributeChanges(cart, hasPhoto);
      if (Object.keys(changes).length) await updateAttributes(changes);
    } catch (e) {
      // ignore
    }
  }

  async function syncMiniPhotoAttributes(hasPhoto, cart) {
    const message = hasPhoto ? 'Voorbeeld binnen 3 uur via WhatsApp / e-mail.' : '';
    const flag = hasPhoto ? '1' : '';
    const expected = {
      'Foto check': message,
      photo_check: flag
    };
    const changes = {};
    Object.keys(expected).forEach(key => {
      if (String((cart.attributes || {})[key] || '') !== expected[key]) changes[key] = expected[key];
    });
    if (Object.keys(changes).length) await updateAttributes(changes);
  }

  function syncMiniPhotoCheckbox(cart) {
    const hasPhoto = cartHasPhotoCheck(cart);
    document.querySelectorAll('#dp4y-minicart-fotocheck, #dp4y-cart-fotocheck').forEach(cb => {
      cb.checked = pendingPhotoChoice ? pendingPhotoChoice.desired : hasPhoto;
      cb.disabled = false;
    });
  }

  async function refreshMiniCart() {
    const bt = typeof BoosterTheme !== 'undefined' ? BoosterTheme : window.BoosterTheme;
    if (
      bt && bt.cart && typeof bt.cart.dispatchListeners === 'function'
    ) {
      await bt.cart.dispatchListeners({ change: true });
    }
    if (typeof cartPage !== 'undefined' && typeof cartPage.updateCart === 'function') {
      await cartPage.updateCart();
    }
  }

  function reportPhotoChoiceError() {
    const id = 'dp4y-photo-choice-error';
    let status = document.getElementById(id);
    if (!status) {
      status = document.createElement('p');
      status.id = id;
      status.setAttribute('role', 'alert');
      const controls = document.querySelectorAll('#dp4y-minicart-fotocheck, #dp4y-cart-fotocheck');
      const visible = Array.from(controls).find(cb => cb.getClientRects().length);
      (visible ? visible.parentElement : document.body).appendChild(status);
    }
    status.textContent = 'Je Foto Check-keuze kon niet worden bevestigd. Controleer je winkelwagen en kies opnieuw. Er moet een product in je winkelwagen staan.';
  }

  function handleMiniPhotoCheckChange(event) {
    const cb = event.target;
    if (!cb || (cb.id !== 'dp4y-minicart-fotocheck' && cb.id !== 'dp4y-cart-fotocheck')) return;
    // The cart page also binds a target listener. Own only these two controls.
    event.stopImmediatePropagation();
    pendingPhotoChoice = { desired: !!cb.checked };
    const status = document.getElementById('dp4y-photo-choice-error');
    if (status) status.remove();
    return processPhotoChoice();
  }

  async function processPhotoChoice() {
    if (miniPhotoToggleBusy || enforcing || !pendingPhotoChoice) return;
    const variantId = Number((window.DP4Y_CART_RULES || {}).photoCheckVariantId || 0);
    if (!variantId) {
      pendingPhotoChoice = null;
      reportPhotoChoiceError();
      return;
    }
    miniPhotoToggleBusy = true;
    try {
      while (pendingPhotoChoice) {
        let cart = await fetchCart();
        // Read the LAST choice after the delayed GET, not a detached checkbox.
        const desired = pendingPhotoChoice.desired;
        pendingPhotoChoice = null;
        if (desired && !getOtherItems(cart).length) {
          throw new Error('No main product for photo check');
        }
        if (desired && !cartHasPhotoCheck(cart)) await addVariant(variantId, 1);
        if (!desired && cartHasPhotoCheck(cart)) await removePhotoCheckFromCart(cart);
        cart = await fetchCart();
        if (pendingPhotoChoice) continue;
        if (cartHasPhotoCheck(cart) !== desired) throw new Error('Photo choice not confirmed');
        await syncMiniPhotoAttributes(desired, cart);
        const confirmedCart = await fetchCart();
        syncMiniPhotoCheckbox(confirmedCart);
        await refreshMiniCart();
      }
    } catch (e) {
      // Do not retry an uncertain write. Make an unconfirmed choice visible.
      pendingPhotoChoice = null;
      console.warn('[DP4Y] Photo check choice not confirmed');
      try {
        const cart = await fetchCart();
        syncMiniPhotoCheckbox(cart);
        await refreshMiniCart();
      } catch (err) { /* The visible message remains the failure outcome. */ }
      reportPhotoChoiceError();
    } finally {
      miniPhotoToggleBusy = false;
      if (pendingPhotoChoice) await processPhotoChoice();
      else scheduleCartRules();
    }
  }

  function scheduleCartRules() {
    clearTimeout(enforceTimer);
    enforceTimer = setTimeout(() => enforceCartRules(true), 80);
  }

  async function enforceCartRules(orphanOnly = false) {
    if (enforcing || miniPhotoToggleBusy) {
      enforcePending = true;
      return;
    }
    enforcing = true;
    enforcePending = false;
    try {
      const cart = await fetchCart();

      // Only owned photo lines/attributes are changed in the orphan path.
      // Other merchandise (including unmarked upsells) counts as a main item.
      if (await removeOrphanPhotoCheck(cart)) return;
      // New event hooks do not broaden legacy max-1/free-stones enforcement.
      if (orphanOnly) return;

      // 1) Photo check max 1 + sync active state
      const photoRes = await enforcePhotoCheck(cart);
      if (photoRes.changed) return;

      // Keep attribute in sync even if no cart mutations were needed
      await syncPhotoActiveFlag(cartHasPhotoCheck(cart), cart);

      // 2) Free stones rule
      const stonesChanged = await enforceFreeStones(cart);
      if (stonesChanged) return;
    } catch (e) {
      console.warn('[DP4Y] Cart rules could not be confirmed');
    } finally {
      enforcing = false;
      if (pendingPhotoChoice) await processPhotoChoice();
      else if (enforcePending) scheduleCartRules();
    }
  }

  function attach() {
    // Run once on load
    enforceCartRules();

    // Booster dispatches these DOM events after completed cart requests.
    ['booster:cart:add', 'booster:cart:update', 'booster:cart:refresh', 'booster:cart:open']
      .forEach(name => document.addEventListener(name, scheduleCartRules));

    // Fallback triggers: after common cart actions
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!t) return;
      if (
        t.closest('[name="add"]') ||
        t.closest('button[name="plus"]') ||
        t.closest('button[name="minus"]') ||
        t.closest('[data-cart-update]') ||
        t.closest('a[href*="/cart"]') ||
        t.closest('button')
      ) {
        setTimeout(enforceCartRules, 450);
      }
    });

    document.addEventListener('change', handleMiniPhotoCheckChange, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();

(function () {
  'use strict';

  const selector = '.dp4y-drawer-services';
  const mobile = window.matchMedia('(max-width: 768px)');
  let collapsed = false;

  function apply(box) {
    const compact = mobile.matches && collapsed;
    box.classList.toggle('dp4y-services-collapsed', compact);
    box.classList.toggle('dp4y-services-has-photo', !!box.querySelector('#dp4y-minicart-fotocheck'));
    box.setAttribute('data-dp4y-services-ready', 'true');
    const button = box.querySelector('[data-dp4y-services-toggle]');
    if (button) button.setAttribute('aria-expanded', String(!compact));
  }

  function refresh() {
    document.querySelectorAll(selector).forEach(apply);
  }

  function init() {
    refresh();
    document.addEventListener('click', function (event) {
      const target = event.target instanceof Element ? event.target : null;
      const button = target && target.closest('[data-dp4y-services-toggle]');
      if (!button || !button.closest(selector) || !mobile.matches) return;
      event.preventDefault();
      event.stopPropagation();
      collapsed = !collapsed;
      refresh();
    }, true);

    mobile.addEventListener('change', refresh);
    new MutationObserver(function (records) {
      const replaced = records.some(function (record) {
        return Array.from(record.addedNodes).some(function (node) {
          return node instanceof Element && (node.matches(selector) || node.querySelector(selector));
        });
      });
      if (replaced) refresh();
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
